import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import smsService from '../services/smsService';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiX, FiMessageSquare, FiCheck, FiAlertTriangle, FiCalendar, FiRefreshCw, FiClock } = FiIcons;

    const SmsLogsModal = ({ onClose }) => {
      const [smsLogs, setSmsLogs] = useState([]);
      const [loading, setLoading] = useState(true);

      useEffect(() => {
        loadSmsLogs();
      }, []);

      const loadSmsLogs = async () => {
        setLoading(true);
        try {
          const logs = await smsService.getSmsLogs();
          setSmsLogs(logs);
        } catch (error) {
          console.error('Error loading SMS logs:', error);
        } finally {
          setLoading(false);
        }
      };

      const getStatusInfo = (status) => {
        const statuses = {
          'sent': { color: 'bg-green-100 text-green-800', icon: FiCheck, label: 'Sent' },
          'pending': { color: 'bg-yellow-100 text-yellow-800', icon: FiClock, label: 'Pending' },
          'failed': { color: 'bg-red-100 text-red-800', icon: FiAlertTriangle, label: 'Failed' },
        };
        return statuses[status] || { color: 'bg-gray-100 text-gray-800', icon: FiMessageSquare, label: status };
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">SMS Logs</h2>
                <p className="text-sm text-gray-600 mt-1">Status of sent SMS messages (via Twilio).</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={loadSmsLogs} disabled={loading} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                  <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} /> Refresh
                </button>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  'Pending' status means the SMS has been queued for a backend service to send. The backend should update the status to 'Sent' or 'Failed'.
                </p>
              </div>
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading SMS logs...</p>
                </div>
              ) : smsLogs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {smsLogs.map((log) => {
                        const statusInfo = getStatusInfo(log.status);
                        return (
                          <tr key={log.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{log.to}</div>
                              <div className="text-sm text-gray-500">From: {log.from}</div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-700 max-w-md truncate">{log.body}</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{new Date(log.sentAt).toLocaleDateString()}</div>
                              <div className="text-sm text-gray-500">{new Date(log.sentAt).toLocaleTimeString()}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                                <SafeIcon icon={statusInfo.icon} /> {statusInfo.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <SafeIcon icon={FiMessageSquare} className="text-gray-400 text-4xl mx-auto mb-4" />
                  <p className="text-gray-500">No SMS messages have been logged yet.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t">
              <button onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Close</button>
            </div>
          </motion.div>
        </div>
      );
    };
    export default SmsLogsModal;